import React, { useEffect, useState } from 'react';
import './HomePage.css';

const TIKTOK_EMBED_SRC = 'https://www.tiktok.com/embed.js';

/**
 * Loads TikTok's embed script, which turns the <blockquote class="tiktok-embed">
 * placeholders in this page into real embeds.
 *
 * This used to live in public/index.html, which meant every route paid for the
 * request even though only this page has embeds. Injected here instead, guarded
 * so a remount doesn't add a second copy. The script is deliberately left in
 * the DOM on unmount: removing it wouldn't un-render the embeds it already
 * created, and re-adding it on the next visit would re-run third-party code.
 */
const useTikTokEmbeds = () => {
  useEffect(() => {
    if (document.querySelector(`script[src="${TIKTOK_EMBED_SRC}"]`)) return;

    const script = document.createElement('script');
    script.src = TIKTOK_EMBED_SRC;
    script.async = true;
    document.body.appendChild(script);
  }, []);
};

// FAQ Accordion Item Component
interface FAQItemProps {
  question: string;
  answer: string;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="faq-item">
      <button
        className={`faq-question ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{question}</span>
        <span className="faq-icon">{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && (
        <div className="faq-answer">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

const HomePage: React.FC = () => {
  useTikTokEmbeds();

  return (
    <div className="homepage">
      {/* Hero Section with Video */}
      <section className="hero-section">
        {/* Navigation Header */}
        <div className="hero-nav">
          <div className="nav-container">
            <div className="nav-logo">
              <img src="/rally-font.svg" alt="Rally" className="rally-logo" />
            </div>
            <div className="nav-app-store">
              <a href="https://apps.apple.com/us/app/rally-voice-notes-on-race-day/id6746744579" className="app-store-button" target="_blank" rel="noopener noreferrer">
                <img src="/app-store-dark.svg" alt="Download on App Store" className="app-store-badge" />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.wimlabsllc.rallyapp" className="app-store-button" target="_blank" rel="noopener noreferrer">
                <img src="/google-play-badge.png" alt="Get it on Google Play" className="app-store-badge" />
              </a>
            </div>
          </div>
        </div>

        {/* Video Background */}
        <div className="hero-video-container">
          <video 
            className="hero-video"
            autoPlay 
            muted 
            loop 
            playsInline
          >
            <source src="/hero-video.mp4" type="video/mp4" />
          </video>
          
          {/* Video overlay content */}
          <div className="hero-overlay">
            {/* Mile 4 SVG overlay */}
            <img 
              src="/mile4-hero.svg" 
              alt="Mile 4" 
              className="hero-svg mile4-svg"
            />
            {/* Mile 13 SVG overlay */}
            <img 
              src="/mile13-hero.svg" 
              alt="Mile 13" 
              className="hero-svg mile13-svg"
            />
            {/* Hero text during 30-second window */}
            <div className="hero-text">
              <div className="hero-text-line1">Voice notes from your family and friends</div>
              <div className="hero-text-line2">to keep you going at every mile</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section">
        <h2 className="section-title how-it-works-title">How It Works</h2>
        
        <div className="how-it-works-grid">
          {/* Step 1 */}
          <div className="how-it-works-card">
            <h3 className="step-title">Choose your race</h3>
            <img src="/steps/step-1.svg" alt="Step 1" className="step-icon" />
            <p className="step-description">
              Select from events across the<br />US, Canada, Europe, and Australia
            </p>
          </div>

          {/* Step 2 */}
          <div className="how-it-works-card">
            <h3 className="step-title">Share with friends</h3>
            <img src="/steps/step-2.svg" alt="Step 2" className="step-icon" />
            <p className="step-description">
              Friends and family can record you messages<br />at specific mile markers
            </p>
          </div>

          {/* Step 3 */}
          <div className="how-it-works-card">
            <h3 className="step-title">Enjoy race day!</h3>
            <img src="/steps/step-3.svg" alt="Step 3" className="step-icon" />
            <p className="step-description">
              Voice notes automatically play giving you<br />encouragement at every mile
            </p>
          </div>
        </div>
      </section>

      <div className="homepage-container">
        {/* COMMENTED OUT - Upcoming Events Section (backup in case FAQ needs to be reverted)
        <section className="upcoming-events-section">
          <h2 className="section-title">Upcoming Events</h2>

          <div className="marathon-carousel">
            <div className="marathon-track">
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/tcs-sydney-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3>TCS Sydney Marathon</h3>
                  <p>August 31, 2025</p>
                </div>
              </div>
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/bmw-berlin-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3>BMW Berlin Marathon</h3>
                  <p>September 21, 2025</p>
                </div>
              </div>
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/long-beach-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3>Long Beach Marathon</h3>
                  <p>October 5, 2025</p>
                </div>
              </div>
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/chicago-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3>Chicago Marathon</h3>
                  <p>October 12, 2025</p>
                </div>
              </div>
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/marine-corps-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3>Marine Corps Marathon</h3>
                  <p>October 26, 2025</p>
                </div>
              </div>
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/raleigh-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3>Raleigh Marathon</h3>
                  <p>November 2, 2025</p>
                </div>
              </div>
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/tcs-nyc-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3>TCS NYC Marathon</h3>
                  <p>November 2, 2025</p>
                </div>
              </div>
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/every-womans-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3 className="long-title">Every Woman's Marathon</h3>
                  <p>November 16, 2025</p>
                </div>
              </div>
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/philadelphia-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3>Philadelphia Marathon</h3>
                  <p>November 23, 2025</p>
                </div>
              </div>

              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/tcs-sydney-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3>TCS Sydney Marathon</h3>
                  <p>August 31, 2025</p>
                </div>
              </div>
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/bmw-berlin-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3>BMW Berlin Marathon</h3>
                  <p>September 21, 2025</p>
                </div>
              </div>
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/long-beach-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3>Long Beach Marathon</h3>
                  <p>October 5, 2025</p>
                </div>
              </div>
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/chicago-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3>Chicago Marathon</h3>
                  <p>October 12, 2025</p>
                </div>
              </div>
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/marine-corps-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3>Marine Corps Marathon</h3>
                  <p>October 26, 2025</p>
                </div>
              </div>
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/raleigh-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3>Raleigh Marathon</h3>
                  <p>November 2, 2025</p>
                </div>
              </div>
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/tcs-nyc-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3>TCS NYC Marathon</h3>
                  <p>November 2, 2025</p>
                </div>
              </div>
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/every-womans-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3 className="long-title">Every Woman's Marathon</h3>
                  <p>November 16, 2025</p>
                </div>
              </div>
              <div className="marathon-card" style={{backgroundImage: 'url(/marathons/philadelphia-marathon.png)'}}>
                <div className="marathon-overlay">
                  <h3>Philadelphia Marathon</h3>
                  <p>November 23, 2025</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        END COMMENTED OUT SECTION */}

        {/* TikTok Section */}
        <section className="tiktok-section">
          <h2 className="section-title tiktok-title">Hear Rally on Race Day</h2>

          <div className="tiktok-grid">
            <blockquote className="tiktok-embed" cite="https://www.tiktok.com/@ellayyvee/video/7559035905554648333" data-video-id="7559035905554648333" style={{maxWidth: '605px', minWidth: '325px'}}>
              <section>
                <a target="_blank" title="@ellayyvee" href="https://www.tiktok.com/@ellayyvee?refer=embed">@ellayyvee</a> this is your sign to download the @Rally 🥇 app and ask your loved ones to leave you voice memos for your first marathon 🥹🫶🏼 this really really got me through - I loved it SO much. I have the best support team ever and absolutely did not do this alone. 💛 <a title="marathon" target="_blank" href="https://www.tiktok.com/tag/marathon?refer=embed">#marathon</a> <a title="firstmarathon" target="_blank" href="https://www.tiktok.com/tag/firstmarathon?refer=embed">#firstmarathon</a> <a title="runtok" target="_blank" href="https://www.tiktok.com/tag/runtok?refer=embed">#runtok</a> <a title="milwaukee" target="_blank" href="https://www.tiktok.com/tag/milwaukee?refer=embed">#milwaukee</a> <a target="_blank" title="♬ original sound - WBsongs" href="https://www.tiktok.com/music/original-sound-7447237948250409750?refer=embed">♬ original sound - WBsongs</a>
              </section>
            </blockquote>

            <blockquote className="tiktok-embed" cite="https://www.tiktok.com/@sammydruns/video/7545285294942113055" data-video-id="7545285294942113055" style={{maxWidth: '605px', minWidth: '325px'}}>
              <section>
                <a target="_blank" title="@sammydruns" href="https://www.tiktok.com/@sammydruns?refer=embed">@sammydruns</a> What an experience! @TCS Sydney Marathon  @Rally 🥇 <a title="marathon" target="_blank" href="https://www.tiktok.com/tag/marathon?refer=embed">#marathon</a> <a target="_blank" title="♬ original sound - Sammy like to run" href="https://www.tiktok.com/music/original-sound-7545285253699586846?refer=embed">♬ original sound - Sammy like to run</a>
              </section>
            </blockquote>

            <blockquote className="tiktok-embed" cite="https://www.tiktok.com/@lifewithlibbymd/video/7545789169134603541" data-video-id="7545789169134603541" style={{maxWidth: '605px', minWidth: '325px'}}>
              <section>
                <a target="_blank" title="@lifewithlibbymd" href="https://www.tiktok.com/@lifewithlibbymd?refer=embed">@lifewithlibbymd</a> being able to listen to voice notes from my fav people in the world was such a blessing…video about the app i used coming soon !!  @TCS Sydney Marathon  @Rally 🥇  <a title="sydneymarathon" target="_blank" href="https://www.tiktok.com/tag/sydneymarathon?refer=embed">#sydneymarathon</a> <a title="sm25" target="_blank" href="https://www.tiktok.com/tag/sm25?refer=embed">#sm25</a> <a title="raceday" target="_blank" href="https://www.tiktok.com/tag/raceday?refer=embed">#raceday</a> <a title="marathonrunner" target="_blank" href="https://www.tiktok.com/tag/marathonrunner?refer=embed">#marathonrunner</a> <a title="rally" target="_blank" href="https://www.tiktok.com/tag/rally?refer=embed">#rally</a> <a target="_blank" title="♬ Two Weeks - Grizzly Bear" href="https://www.tiktok.com/music/Two-Weeks-6771810672880650241?refer=embed">♬ Two Weeks - Grizzly Bear</a>
              </section>
            </blockquote>

            <blockquote className="tiktok-embed" cite="https://www.tiktok.com/@abbsd1/video/7566840136193821983" data-video-id="7566840136193821983" style={{maxWidth: '605px', minWidth: '325px'}}>
              <section>
                <a target="_blank" title="@abbsd1" href="https://www.tiktok.com/@abbsd1?refer=embed">@abbsd1</a>
              </section>
            </blockquote>

            <blockquote className="tiktok-embed" cite="https://www.tiktok.com/@hollynewbs/video/7555476687698349334" data-video-id="7555476687698349334" style={{maxWidth: '605px', minWidth: '325px'}}>
              <section>
                <a target="_blank" title="@hollynewbs" href="https://www.tiktok.com/@hollynewbs?refer=embed">@hollynewbs</a> all the rehab &#38; training paid off and the support couldn't have been any more amazing, I love you all so much😭 @Rally 🥇 @RunThrough | Running Community <a title="swindonhalfmarathon" target="_blank" href="https://www.tiktok.com/tag/swindonhalfmarathon?refer=embed">#swindonhalfmarathon</a> <a title="halfmarathon" target="_blank" href="https://www.tiktok.com/tag/halfmarathon?refer=embed">#halfmarathon</a> <a target="_blank" title="♬ suono originale - swami" href="https://www.tiktok.com/music/suono-originale-7275373424997239585?refer=embed">♬ suono originale - swami</a>
              </section>
            </blockquote>

            <blockquote className="tiktok-embed" cite="https://www.tiktok.com/@woahhenni/video/7560349859174370590" data-video-id="7560349859174370590" style={{maxWidth: '605px', minWidth: '325px'}}>
              <section>
                <a target="_blank" title="@woahhenni" href="https://www.tiktok.com/@woahhenni?refer=embed">@woahhenni</a> my village, my ppl, I love you sm <a title="chicagomarathon" target="_blank" href="https://www.tiktok.com/tag/chicagomarathon?refer=embed">#chicagomarathon</a> <a title="runner" target="_blank" href="https://www.tiktok.com/tag/runner?refer=embed">#runner</a> <a title="fyp" target="_blank" href="https://www.tiktok.com/tag/fyp?refer=embed">#fyp</a> <a target="_blank" title="♬ End of Beginning - Djo" href="https://www.tiktok.com/music/End-of-Beginning-Djo-7302500653593561898?refer=embed">♬ End of Beginning - Djo</a>
              </section>
            </blockquote>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <h2 className="section-title faq-title">FAQ</h2>

          <div className="faq-container">
            <FAQItem
              question="What do I have to do before race day?"
              answer="Download Rally: Voice Notes on Race Day from the app store! Find and join your race. Share the link with family and friends."
            />
            <FAQItem
              question="Do people recording voice notes need to download the app?"
              answer="No! They can record voice notes right from their phone, desktop, or laptop."
            />
            <FAQItem
              question="What do I do on Race Day?"
              answer="Always make sure you're on the latest version of the app! We are making improvements every week and it's best to update so you have the best race day experience. When you're about to start your run, tap 'Start Race'. Rally will then ask you to enable location permissions. It's important you select 'Always Allow' so Rally works in the background."
            />
            <FAQItem
              question="Can I have other apps open with Rally?"
              answer="Yes! You can have your personal fitness app and/or music apps open as well. Rally runs in the background :)"
            />
            <FAQItem
              question="Will I still hear my voice notes if I'm listening to music?"
              answer="Yes! Rally will automatically adjust Spotify, Apple Music, YouTube Music etc. to a lower volume while playing your voice notes. Once your voice note is done playing, your music volume will return to normal levels. You don't have to do or touch a thing!"
            />
            <FAQItem
              question="Can I set this up for someone else?"
              answer="Yes! We are currently building this out to be much easier but we have the ability to transfer races between accounts. Just DM us on IG or TikTok or send us an email - hello@runwithrally.com and we'll assist."
            />
            <FAQItem
              question="If I don't see my race, will you add it?"
              answer="Of course! Just DM or email us - hello@runwithrally.com"
            />
          </div>
        </section>

        {/* Don't see your race? Section */}
        <section className="contact-section">
          <h2 className="contact-title">Don't see your race?</h2>
          <p className="contact-subtitle">Tell us. We're adding new races every day!</p>
          <a
            href="mailto:hello@runwithrally.com?subject=New%20Race%20for%20the%20Rally%20app"
            className="contact-button"
          >
            Get in touch
          </a>
        </section>
      </div>
    </div>
  );
};

export default HomePage;